// Front desk — the screen the practice runs the day from.
//
// [15:56] "I have my front desk, which is, it shows me all the clients in a
// list." In Boulevard it is a board of today's visits by status, and it is
// the first thing in the nav for a reason: the person on the desk lives here.
//
// Three tabs, because all three are daily jobs and all three came out of the
// recording:
//   • Today       — the status board
//   • Follow-ups  — the internal queue she bends the waitlist into [23:38]
//   • Paperwork   — the day list + the medical director's review pile [26:03]

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList, ChevronLeft, ChevronRight, Clock, AlertTriangle, Star,
  FileText, Plus, Check, Undo2, Stethoscope, Phone, CalendarPlus, X, CircleCheck, UserX,
  Armchair, Pencil,
} from "lucide-react";
import {
  Skeleton, useToast, EmptyState, Button, Modal, ModalFooter, Input, Textarea, Select, Badge,
  useConfirm,
} from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { formatTime, formatDate, formatPlainDate, formatPhone, formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SEAT_CLASSES } from "@/lib/seat-colors";
import { TimeClock } from "@/features/timeclock/TimeClock";
import { ClockRoster } from "@/features/timeclock/ClockRoster";
import { BookButton } from "@/features/scheduling/BookButton";
import { AppointmentFormModal } from "@/features/scheduling/AppointmentFormModal";
import type { Appointment, Service, Provider } from "@/lib/types";

// ─── Types ─────────────────────────────────────────────────────────────────

interface Segment {
  service: string | null; room: string | null; provider: string | null;
  /** The chair's own colour, so the board matches the calendar's columns. */
  room_color?: string | null;
  starts_at: string; ends_at: string;
}
interface Card {
  /** Held while the client types a card in - not a booking yet. ISO deadline. */
  holding_for_card?: string | null;
  id: string; title: string; status: string; walk_in: boolean;
  starts_at: string; ends_at: string; contact_id: string | null;
  client_name: string; phone: string | null; scheduling_alert: string | null;
  /** Her other alerts, and any follow-up that has come due. */
  alerts?: string[];
  tag_emojis?: string;
  is_new_client: boolean; forms_total: number; forms_completed: number;
  segments: Segment[]; paid: boolean; late_minutes: number;
  /** Has this client a card we can charge? "A credit card is required to
   *  secure and hold your appointment." */
  card_on_file?: boolean;
}
type Lanes = Record<string, Card[]>;

interface FollowUp {
  id: string; contact_id: string; reason: string; note: string; status: string;
  urgent: boolean; client_name: string; phone: string | null;
  created_by_name: string | null; created_at: string;
  resolved_by: string | null; resolution: string | null; assigned_name: string | null;
}

interface DayRow {
  id: string; title: string; status: string; starts_at: string; contact_id: string | null;
  client_name: string; phone: string | null; services: string; providers: string;
  forms_total: number; forms_completed: number; forms_awaiting_review: number;
  charts_total: number; charts_signed: number; charts_awaiting_review: number;
}

// Lane order mirrors Boulevard's board so the muscle memory carries.
// No Walk-in lane — her request ("on front desk remove walk in"): a walk-in
// is rare enough for her that a standing empty column cost more than the
// distinction was worth. Walk-ins keep their badge on the card and simply
// sort by status like everyone else.
const LANES: { key: string; label: string; tone: string }[] = [
  { key: "unconfirmed", label: "Unconfirmed", tone: "text-amber-300" },
  { key: "confirmed",   label: "Confirmed",   tone: "text-sky-300" },
  { key: "arrived",     label: "Arrived",     tone: "text-emerald-300" },
  { key: "in_chair",    label: "In the chair",tone: "text-teal-300" },
  { key: "completed",   label: "Done",        tone: "text-zinc-400" },
];

/** Clock time in the practice's own timezone, e.g. "10:30 AM". */
const timeIn = (iso: string, tz: string | null) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric", minute: "2-digit", timeZone: tz ?? undefined,
    }).format(new Date(iso));
  } catch { return formatTime(iso); }
};

/**
 * "On the follow up tab the drop down selections I need: Provider Review /
 *  Provider Review Labs / Recommendation Letters / Immediate Follow Up /
 *  Nurse Review / Front Desk Review"
 *
 * In her order. The older four are kept only so follow-ups raised before this
 * still say what they are; they are not offered any more.
 */
const REASONS: { key: string; label: string }[] = [
  { key: "provider_review", label: "Provider Review" },
  { key: "provider_review_labs", label: "Provider Review Labs" },
  { key: "recommendation_letters", label: "Recommendation Letters" },
  { key: "immediate_follow_up", label: "Immediate Follow Up" },
  { key: "nurse_review", label: "Nurse Review" },
  { key: "front_desk_review", label: "Front Desk Review" },
];
const REASON_LABEL: Record<string, string> = {
  ...Object.fromEntries(REASONS.map((r) => [r.key, r.label])),
  lab_review: "Lab review", needs_call: "Needs a call",
  follow_up: "Follow up", other: "Other",
};

// ─── Page ──────────────────────────────────────────────────────────────────

export function FrontDeskPage() {
  const [tab, setTab] = useState<"today" | "followups" | "paperwork" | "consults" | "insurance">("today");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Front desk</h1>
        <div className="flex gap-1 rounded-lg border border-zinc-800 bg-zinc-900/50 p-0.5">
          {/* "can be on the front desk by follow ups and paperwork" — the
              insurance flow sheet is a desk job, not a report. */}
          {([["today", "Today"], ["followups", "Follow-ups"], ["paperwork", "Paperwork"], ["consults", "Consults due"], ["insurance", "Insurance"]] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm",
                tab === k ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        {/* "Can we tuck the clock in, all the way right under consults due, no
            need for that long thing." Clocking in is the first thing anybody
            does and then never again that day, so it sits in the corner under
            the tabs rather than across the top of the clients. */}
        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
          <TimeClock compact />
        </div>
      </div>

      <div className="flex justify-end">
        <div className="w-full sm:max-w-md">
          <ClockRoster defaultOpen={false} />
        </div>
      </div>

      {tab === "today" && <Board />}
      {tab === "followups" && <FollowUpQueue />}
      {tab === "paperwork" && <Paperwork />}
      {tab === "consults" && <ConsultsDue />}
      {tab === "insurance" && <InsuranceFlow />}
    </div>
  );
}


// ─── The insurance flow sheet ──────────────────────────────────────────────
//
//   "Tracking needs to have a main hub with a flow sheet feel. To log name and
//    payments received the 25 dollars and the appointments that are getting
//    claimed with the 5 dollar fee and a starting of deductible owed and
//    deductible met as we go thru once deductible is met … I'll need a flow
//    sheet that tally's into a main flow but individual in each chart … Then
//    the main will show totals And amounts payable To the biller based on fees
//    … the main would show us and separate new bookings from ones we already
//    tracked"
//
// One line per client for the year, the tally underneath it, and below that
// the visits booked onto an insurance treatment that nobody has put on a
// sheet yet. The fees are worked out on the server from her rules, so this
// screen and a client's own chart can never give two answers.

interface FlowRow {
  contact_id: string;
  client_name: string;
  carrier: string;
  network: string;
  deductible_cents: number;
  deductible_met_cents: number;
  deductible_remaining_cents: number;
  deductible_state: "none" | "not_met" | "partial" | "met";
  claims: number;
  charged_cents: number;
  insurance_paid_cents: number;
  patient_paid_cents: number;
  verification_cents: number;
  verification_paid_on: string | null;
  claim_fees_cents: number;
  short_year_fee_cents: number;
  short_year_provisional: boolean;
  pending_fees: number;
  biller_total_cents: number;
  last_service_date: string | null;
}

interface FlowUntracked {
  appointment_id: string;
  contact_id: string;
  client_name: string;
  starts_at: string;
  /** The day they were seen, in the practice's own timezone. */
  service_date: string;
  status: string;
  service_name: string;
  charge_cents: number;
  on_file: boolean;
}

interface Flow {
  year: number;
  this_year: number;
  rules: {
    verificationCents: number;
    claimFeeCents: number;
    shortYearFeeCents: number;
    minClaimsPerYear: number;
    afterDeductiblePct: number;
  };
  rows: FlowRow[];
  /** How many treatments are ticked as billing insurance. Zero means the
   *  untracked list below can never fill, whatever is booked. */
  insurance_services: number;
  totals: {
    clients: number;
    clients_on_file: number;
    claims: number;
    charged_cents: number;
    insurance_paid_cents: number;
    patient_paid_cents: number;
    verification_cents: number;
    claim_fees_cents: number;
    short_year_fee_cents: number;
    pending_fees: number;
    biller_total_cents: number;
  };
  untracked: FlowUntracked[];
}

/** The one number she opens this screen for, four ways. */
function FlowStat({ label, value, sub, tone }: {
  label: string; value: string; sub?: string; tone?: "amber" | "emerald";
}) {
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/40 px-4 py-3">
      <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={cn("mt-1 text-xl font-semibold tabular-nums",
        tone === "amber" ? "text-amber-300" : tone === "emerald" ? "text-emerald-300" : "text-zinc-100")}>
        {value}
      </p>
      {sub && <p className="mt-0.5 text-[11px] text-zinc-500">{sub}</p>}
    </div>
  );
}

function DeductibleBar({ row }: { row: FlowRow }) {
  if (row.deductible_cents === 0) {
    return <span className="text-[11px] text-zinc-600">not recorded</span>;
  }
  const pct = Math.min(100, Math.round((row.deductible_met_cents / row.deductible_cents) * 100));
  const tone = row.deductible_state === "met" ? "bg-emerald-500"
    : row.deductible_state === "not_met" ? "bg-rose-500" : "bg-amber-500";
  return (
    <div className="min-w-[8rem]">
      <div className="flex items-baseline justify-between gap-2 text-[11px] tabular-nums">
        <span className={cn(row.deductible_state === "met" ? "text-emerald-300" : "text-zinc-300")}>
          {formatMoney(row.deductible_met_cents)}
        </span>
        <span className="text-zinc-600">of {formatMoney(row.deductible_cents)}</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-zinc-800">
        <div className={cn("h-full rounded-full", tone)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function InsuranceFlow() {
  const toast = useToast();
  const [flow, setFlow] = useState<Flow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState<number | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setFlow(await api.get<Flow>("/insurance/hub/flow", year ? { year: String(year) } : undefined));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load the flow sheet");
    }
  }, [year]);

  useEffect(() => { void load(); }, [load]);

  /* "the main would show us and separate new bookings from ones we already
     tracked" — and then let her track one without leaving the page. */
  const track = async (u: FlowUntracked) => {
    setAdding(u.appointment_id);
    try {
      await api.post("/insurance/entries", {
        contact_id: u.contact_id,
        appointment_id: u.appointment_id,
        service_name: u.service_name,
        date_of_service: u.service_date,
        charge_cents: u.charge_cents,
      });
      toast.success("On the sheet", `${u.client_name}'s visit is being tracked.`);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add that visit");
    } finally {
      setAdding(null);
    }
  };

  if (error) return <p className="text-sm text-rose-300">{error}</p>;
  if (!flow) return <Skeleton className="h-64" />;

  const t = flow.totals;
  const years = [flow.this_year, flow.this_year - 1, flow.this_year - 2];

  return (
    <div className="space-y-4">
      {/* The tally first: it is the number she opens this for. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FlowStat label="Owed to the biller" value={formatMoney(t.biller_total_cents)} tone="amber"
          sub={t.short_year_fee_cents > 0
            ? `includes ${formatMoney(t.short_year_fee_cents)} in short-year fees`
            : `${t.claims} ${t.claims === 1 ? "claim" : "claims"} at ${formatMoney(flow.rules.claimFeeCents)} or ${flow.rules.afterDeductiblePct}%`} />
        <FlowStat label="Billed to insurers" value={formatMoney(t.charged_cents)}
          sub={`${formatMoney(t.insurance_paid_cents)} paid back so far`} />
        <FlowStat label="Verifications taken" value={formatMoney(t.verification_cents)} tone="emerald"
          sub="the fee clients pay you up front" />
        <FlowStat label="Clients billing" value={String(t.clients)}
          sub={`${t.clients_on_file} with a policy on file`} />
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-zinc-200">Flow sheet · {flow.year}</h2>
          <Select value={String(flow.year)} onChange={(e) => setYear(Number(e.target.value))}
            className="w-28" aria-label="Which year">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
        </div>
        {flow.rows.length === 0 ? (
          <EmptyState
            title="Nobody is being billed through insurance yet"
            description="Add a policy on a client's chart and their visits flow through here."
          />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-zinc-800/70">
            <table className="w-full min-w-[920px] border-collapse text-xs">
              <thead className="bg-zinc-900/60 text-left text-[11px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Client</th>
                  <th className="px-3 py-2 text-left font-medium">Insurance</th>
                  <th className="px-3 py-2 text-left font-medium">Deductible</th>
                  <th className="px-3 py-2 text-right font-medium">Claims</th>
                  <th className="px-3 py-2 text-right font-medium">Billed</th>
                  <th className="px-3 py-2 text-right font-medium">Insurance paid</th>
                  <th className="px-3 py-2 text-right font-medium">Verification</th>
                  <th className="px-3 py-2 text-right font-medium">Owed to biller</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/50">
                {flow.rows.map((r) => (
                  <tr key={r.contact_id} data-insurance-row className="hover:bg-zinc-900/40">
                    <td className="px-3 py-2">
                      <Link to={`/contacts/${r.contact_id}`} className="text-zinc-100 hover:text-sky-300">
                        {r.client_name}
                      </Link>
                      {r.last_service_date && (
                        <div className="text-[11px] text-zinc-600">last billed {r.last_service_date}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-300">
                      {r.carrier || <span className="text-zinc-600">not recorded</span>}
                      {r.deductible_state === "met" && (
                        <Badge color="emerald" size="xs" className="ml-1.5">met</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2"><DeductibleBar row={r} /></td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-200">
                      {r.claims}
                      {r.short_year_fee_cents > 0 && (
                        <div className="text-[10px] text-amber-400/80">
                          under {flow.rules.minClaimsPerYear}
                          {r.short_year_provisional && " so far"}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{formatMoney(r.charged_cents)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{formatMoney(r.insurance_paid_cents)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-300">
                      {r.verification_cents > 0 ? formatMoney(r.verification_cents) : <span className="text-zinc-700">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-300">
                      {formatMoney(r.biller_total_cents)}
                      {r.pending_fees > 0 && (
                        <div className="text-[10px] font-normal text-zinc-500">
                          {r.pending_fees} waiting on a payout
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-zinc-700 bg-zinc-900/50 font-semibold text-zinc-100">
                  <td className="px-3 py-2" colSpan={3}>
                    {t.clients} {t.clients === 1 ? "client" : "clients"} billed this year
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{t.claims}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(t.charged_cents)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(t.insurance_paid_cents)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-300">{formatMoney(t.verification_cents)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-300">{formatMoney(t.biller_total_cents)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-zinc-200">Booked but not on a sheet</h2>
        {flow.untracked.length === 0 ? (
          flow.insurance_services === 0 ? (
            <EmptyState
              title="No treatment bills insurance yet"
              description="Tick 'We bill insurance for this' on a treatment in Manage, Menu & seats. Until then nothing can appear here, however it is booked."
            />
          ) : (
            <EmptyState
              title="Every insurance visit this year is being tracked"
              description="Anything newly booked onto an insurance treatment turns up here first."
            />
          )
        ) : (
          <ul className="divide-y divide-zinc-800/50 rounded-xl border border-zinc-800/70">
            {flow.untracked.map((u) => (
              <li key={u.appointment_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
                <Link to={`/contacts/${u.contact_id}`} className="text-sm text-zinc-100 hover:text-sky-300">
                  {u.client_name}
                </Link>
                <span className="text-xs text-zinc-500">{u.service_name}</span>
                <span className="text-xs text-zinc-600">{formatDate(u.starts_at)}</span>
                {!u.on_file && <Badge color="zinc" size="xs">no policy yet</Badge>}
                <span className="ml-auto flex items-center gap-2">
                  <span className="text-xs tabular-nums text-zinc-400">{formatMoney(u.charge_cents)}</span>
                  <Button size="sm" variant="secondary" disabled={adding === u.appointment_id}
                    onClick={() => void track(u)}>
                    Track it
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── The board ─────────────────────────────────────────────────────────────

function Board() {
  const toast = useToast();
  const confirm = useConfirm();
  // null until the server tells us what "today" is where the practice is.
  const [date, setDate] = useState<string | null>(null);
  const [practiceToday, setPracticeToday] = useState<string | null>(null);
  const [tz, setTz] = useState<string | null>(null);
  const [lanes, setLanes] = useState<Lanes>({});
  /** Her switch: when she isn't asking for cards, the board doesn't nag. */
  const [cardExpected, setCardExpected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  /**
   * Changing a visit from the board.
   *
   *   "Also Joseph Moreno I can't update his appointment. I can't add
   *    services. So if somone wanted the consultation and decided to get a
   *    injection I can't add him"
   *
   * She was right: nothing on this screen opened the visit. Joseph was
   * sitting in the chair in front of her and the only way to add a treatment
   * was to leave the desk, find him on the schedule, and change it there.
   * The treatments on a card are now the way in, and it opens the same
   * Change-visit dialog the schedule uses.
   */
  const [changing, setChanging] = useState<Appointment | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const [sv, pr] = await Promise.all([
          api.get<{ services: Service[] }>("/services"),
          api.get<{ providers: Provider[] }>("/providers"),
        ]);
        if (!off) { setServices(sv.services); setProviders(pr.providers); }
      } catch { /* the board still works; the dialog just won't open */ }
    })();
    return () => { off = true; };
  }, []);

  const openVisit = async (id: string) => {
    setBusy(id);
    try {
      const r = await api.get<{ appointment: Appointment }>(`/appointments/${id}`);
      setChanging(r.appointment);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't open that visit");
    } finally { setBusy(null); }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get<{ lanes: Lanes; date: string; timezone: string; card_expected?: boolean }>("/frontdesk/board", date ? { date } : {})
      .then((d) => {
        setLanes(d.lanes);
        setCardExpected(d.card_expected === true);
        setTz(d.timezone);
        // First load: adopt the practice's date as both the view and "today".
        if (!date) { setDate(d.date); setPracticeToday(d.date); }
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Couldn't load the board"))
      .finally(() => setLoading(false));
  }, [date, toast]);

  useEffect(() => { load(); }, [load]);

  // The board is a live view of the room; refresh it while it's on screen.
  useEffect(() => {
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const step = async (id: string, s: string) => {
    setBusy(id);
    try {
      await api.post(`/frontdesk/appointments/${id}/step`, { step: s });
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "That didn't work");
    } finally { setBusy(null); }
  };

  /* "please see me scheduled on front desk I have no cancel button to cancel"
     The board offered one way forward and no way out, so a visit that was
     never going to happen could only be got rid of by going to the calendar
     and finding it there. Same two endings the calendar has, same wording,
     same single place they are written. */
  const close = async (card: Card, status: "cancelled" | "no_show") => {
    const ok = await confirm(status === "cancelled" ? {
      title: "Cancel this visit?",
      description: `${card.title} for ${card.client_name} at ${timeIn(card.starts_at, tz)}.`,
      confirmLabel: "Cancel visit",
      danger: true,
    } : {
      title: "Mark as no-show?",
      description: `${card.client_name} will be recorded as a no-show for this visit.`,
      confirmLabel: "Mark no-show",
      danger: true,
    });
    if (!ok) return;
    setBusy(card.id);
    try {
      await api.patch(`/appointments/${card.id}`, { status });
      toast.success(status === "cancelled" ? "Visit cancelled" : "Marked as no-show");
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "That didn't work");
    } finally { setBusy(null); }
  };

  const shift = (days: number) => {
    if (!date) return;
    // Step by calendar days on a UTC-anchored date so no timezone can shift it.
    const [y, m, dd] = date.split("-").map(Number);
    const d = new Date(Date.UTC(y!, (m ?? 1) - 1, dd ?? 1, 12));
    d.setUTCDate(d.getUTCDate() + days);
    setDate(d.toISOString().slice(0, 10));
  };

  const counts = useMemo(
    () => LANES.map((l) => ({ ...l, n: lanes[l.key]?.length ?? 0 })),
    [lanes],
  );
  const missed = lanes.missed ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => shift(-1)}><ChevronLeft className="h-3.5 w-3.5" /></Button>
        {/* "can the icon that says today on the front desk change to the day of
            the week as well so if it's Tuesday (today) and I go to the next day
            can it say Wednesday then next Thursday" — it still goes back to
            today, it just says which day she is standing on. */}
        <Button variant={date === practiceToday ? "primary" : "secondary"} size="sm"
          title={date === practiceToday ? undefined : "Back to today"}
          onClick={() => practiceToday && setDate(practiceToday)}>
          {date && date !== practiceToday ? formatPlainDate(date, { weekday: "long" }) : "Today"}
        </Button>
        <Button variant="secondary" size="sm" onClick={() => shift(1)}><ChevronRight className="h-3.5 w-3.5" /></Button>
        <Input type="date" value={date ?? ""} onChange={(e) => setDate(e.target.value)} className="h-8 w-40 text-sm" />
        <span className="text-sm text-zinc-500">
          {date && formatPlainDate(date)}
        </span>
        {/* Booking from the desk itself — people are booked while standing
            here, not after a detour to the calendar. */}
        <BookButton className="ml-auto" label="Book a visit" onBooked={load} />
      </div>

      {/* The lanes stay on screen even on a quiet day. Boulevard shows the
          columns regardless, and the desk reads the shape of the day from
          them — an empty board that collapses to a message loses that. */}
      {counts.every((c) => c.n === 0) && missed.length === 0 && !loading && (
        <p className="rounded-lg border border-zinc-800/70 bg-zinc-900/30 px-3 py-2 text-sm text-zinc-500">
          Nothing booked on this day yet.
        </p>
      )}

      {loading ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {counts.map((lane) => (
            <div key={lane.key} className="min-w-0">
              <div className="mb-2 flex items-baseline gap-1.5">
                <h3 className={cn("text-xs font-semibold uppercase tracking-wider", lane.tone)}>{lane.label}</h3>
                <span className="text-xs text-zinc-600">{lane.n}</span>
              </div>
              <div className="space-y-2">
                {(lanes[lane.key] ?? []).map((c) => (
                  <VisitCard key={c.id} card={c} lane={lane.key} tz={tz} busy={busy === c.id} onStep={step} onClose={close} cardExpected={cardExpected} onOpenVisit={openVisit} />
                ))}
                {lane.n === 0 && (
                  <div className="rounded-lg border border-dashed border-zinc-800/70 py-6 text-center text-[11px] text-zinc-700">
                    —
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {missed.length > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-rose-300">
            No-show <span className="text-zinc-600">{missed.length}</span>
          </h3>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {missed.map((c) => <VisitCard key={c.id} card={c} lane="missed" tz={tz} busy={false} onStep={step} onClose={close} cardExpected={cardExpected} onOpenVisit={openVisit} />)}
          </div>
        </div>
      )}

      {/* The same dialog the schedule opens, without "select a time on the
          calendar" - there is no calendar on this screen to point at. */}
      {changing && (
        <AppointmentFormModal
          mode={{ kind: "reschedule", appointment: changing }}
          services={services}
          providers={providers}
          defaultDate={date ?? ""}
          onClose={() => setChanging(null)}
          onSaved={() => { setChanging(null); load(); }}
        />
      )}
    </div>
  );
}

function VisitCard({
  card, lane, tz, busy, onStep, onClose, cardExpected, onOpenVisit,
}: {
  card: Card; lane: string; tz: string | null; busy: boolean;
  onStep: (id: string, step: string) => void;
  onClose: (card: Card, status: "cancelled" | "no_show") => void;
  /** Her switch: only flag a missing card when she's asking for one. */
  cardExpected: boolean;
  /** Open this visit to change what is on it. */
  onOpenVisit: (id: string) => void;
}) {
  const formsOutstanding = card.forms_total > 0 && card.forms_completed < card.forms_total;
  /* Every chair this visit uses, once each, in the order they are used, each
     in its own colour.

     "can the color of the seat be the color of the tab seat in front desk
      this will make it pop and will help us with the colors"

     Same map the calendar columns use, so a chair is one colour everywhere. */
  const seats: { name: string; color: string | null }[] = [];
  for (const sg of card.segments) {
    if (!sg.room || seats.some((x) => x.name === sg.room)) continue;
    seats.push({ name: sg.room, color: sg.room_color ?? null });
  }

  // One primary action per lane — the desk shouldn't have to think.
  const next: { step: string; label: string; icon: typeof Check } | null =
    lane === "unconfirmed" ? { step: "confirm", label: "Confirm", icon: Check }
    : lane === "confirmed" ? { step: "arrive", label: "Arrived", icon: CircleCheck }
    : lane === "arrived" ? { step: "start", label: "Take through", icon: Stethoscope }
    : lane === "in_chair" ? { step: "complete", label: "Finish", icon: Check }
    : null;

  return (
    <div className={cn(
      "rounded-lg border bg-zinc-900/40 p-2.5",
      lane === "missed" ? "border-rose-900/50 opacity-70" : "border-zinc-800/70",
      card.late_minutes > 10 && "border-amber-700/60",
    )}>
      <div className="flex items-start justify-between gap-1.5">
        <div className="min-w-0">
          <p className="flex items-center gap-1 text-xs text-zinc-400">
            <Clock className="h-3 w-3" />
            {timeIn(card.starts_at, tz)}
            {card.late_minutes > 0 && (
              <span className="font-medium text-amber-400">({card.late_minutes}m late)</span>
            )}
          </p>
          {card.contact_id ? (
            <Link
              to={`/contacts/${card.contact_id}`}
              className="block truncate text-sm font-medium text-zinc-100 hover:text-indigo-300"
            >
              {card.client_name}
              {card.tag_emojis ? <span className="ml-1" title="Client tags">{card.tag_emojis}</span> : null}
            </Link>
          ) : (
            <p className="truncate text-sm font-medium text-zinc-100">{card.client_name}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {card.is_new_client && (
            <span title="New client"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /></span>
          )}
          {/* Only on a visit still to happen, and only when it isn't already
              paid for - saying "no card" about someone who has walked out is
              noise. */}
          {/* Being held while they type a card in. Not a booking yet, and it
              goes back on the board by itself if no card arrives - so it says
              so, rather than vanishing later with no explanation. */}
          {card.holding_for_card ? (
            <span
              title={`Holding until ${formatTime(card.holding_for_card)} while they add a card`}
              className="rounded bg-amber-500/20 px-1 text-[10px] font-semibold text-amber-200"
            >
              Holding for card
            </span>
          ) : cardExpected && card.card_on_file === false && !card.paid
            && !["completed", "cancelled", "no_show"].includes(card.status) && (
            <span title="No card on file" className="rounded bg-amber-500/15 px-1 text-[10px] font-semibold text-amber-300">
              No card
            </span>
          )}
          {card.paid && <span title="Paid"><CircleCheck className="h-3.5 w-3.5 text-emerald-500" /></span>}
        </div>
      </div>

      {/* A visit is one appointment with several treatments — show them all,
          in order, because that IS the fix she asked for. The chair is NOT
          on these lines: "Vitamin C SPEC-5,000 mg · Sunroo…" is where her
          treatment names run out of card, so the seat went in its own chip
          below, which is what she asked for ("can we see the seat inside the
          appointment box, right next to the paperwork"). */}
      {/* The treatments are the way into the visit: somebody in the chair
          who asks for an injection as well is changed by clicking what they
          are booked for. */}
      <button
        type="button"
        disabled={busy}
        onClick={() => onOpenVisit(card.id)}
        title="Change the treatments on this visit"
        className="mt-1.5 -mx-1 block w-[calc(100%+0.5rem)] rounded px-1 py-0.5 text-left hover:bg-zinc-800/60 disabled:opacity-50"
      >
        <ul className="space-y-0.5">
          {(card.segments.length ? card.segments : [{ service: card.title, room: null, provider: null, starts_at: card.starts_at, ends_at: card.ends_at }]).map((s, i) => (
            <li key={i} className="truncate text-[11px] text-zinc-400">
              {s.service ?? card.title}
            </li>
          ))}
        </ul>
      </button>

      {/* Red, as she asked: an alert that reads like every other amber badge
          on this card is not an alert. */}
      {/* Everything standing against this client, in ONE red block rather
          than one per line: three separate boxes on a card this size stops
          being an alert and starts being wallpaper. The scheduling alert
          leads, then the chart's other alerts and any follow-up now due —
          she used to type "labs due Oct 2026" into the scheduling alert for
          exactly this reason. */}
      {(card.scheduling_alert || (card.alerts ?? []).length > 0) && (
        <div className="mt-1.5 flex items-start gap-1 rounded bg-rose-500/10 px-1.5 py-1 text-[11px] font-medium leading-snug text-rose-300">
          <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
          <div className="min-w-0 space-y-0.5">
            {card.scheduling_alert && <p>{card.scheduling_alert}</p>}
            {(card.alerts ?? []).map((line) => <p key={line}>{line}</p>)}
          </div>
        </div>
      )}

      {/* Where to take them, and what they still owe, on one line. */}
      {(seats.length > 0 || formsOutstanding) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
          {seats.map((seat) => (
            <span
              key={seat.name}
              className={cn(
                "flex items-center gap-1 rounded px-1.5 py-0.5 font-medium",
                SEAT_CLASSES[seat.color ?? ""] ?? "bg-zinc-800/70 text-zinc-200",
              )}
            >
              <Armchair className="h-3 w-3 shrink-0 opacity-70" />
              {seat.name}
            </span>
          ))}
          {formsOutstanding && (
            <span className="flex items-center gap-1 text-rose-400">
              <FileText className="h-3 w-3" />
              Paperwork {card.forms_completed}/{card.forms_total}
            </span>
          )}
        </div>
      )}

      {lane !== "missed" && (
        <div className="mt-2 flex items-center gap-1">
          {next && (
            <Button size="sm" variant="secondary" disabled={busy}
              className="h-7 flex-1 text-[11px]"
              onClick={() => onStep(card.id, next.step)}>
              <next.icon className="h-3 w-3" /> {next.label}
            </Button>
          )}
          {lane !== "unconfirmed" && (
            <Button size="sm" variant="ghost" disabled={busy} title="Move back"
              className="h-7 px-1.5 text-zinc-500"
              onClick={() => onStep(card.id, "undo")}>
              <Undo2 className="h-3 w-3" />
            </Button>
          )}
          {/* "once a patient is taken to the chair in the software and hit
              finished it no longer allows me to cash it out." Pressing Finish
              moved the card to the Completed lane and took the only way to
              the till with it — so a visit that ended before anybody paid
              could not be paid for at all. Checking out is now offered from
              the moment they arrive until the money is actually taken. */}
          {card.contact_id && !card.paid && ["arrived", "in_chair", "completed"].includes(lane) && (
            <Link to={`/checkout?contact_id=${card.contact_id}&appointment_id=${card.id}`}
              className="rounded-md px-1.5 py-1 text-[11px] font-medium text-indigo-300 hover:bg-zinc-800">
              Check out
            </Link>
          )}
          {/* The two ways a visit ends without happening. On the board as
              well as on the calendar, because the desk is where she finds
              out it is not happening. */}
          {lane !== "completed" && (
            <>
              <Button size="sm" variant="ghost" disabled={busy} title="They did not turn up"
                className="ml-auto h-7 px-1.5 text-zinc-500 hover:text-amber-300"
                onClick={() => onClose(card, "no_show")}>
                <UserX className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} title="Cancel this visit"
                className="h-7 px-1.5 text-zinc-500 hover:text-rose-300"
                onClick={() => onClose(card, "cancelled")}>
                <X className="h-3 w-3" />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Follow-up queue ───────────────────────────────────────────────────────

interface DueFromChart {
  id: string; kind: string; contact_id: string; client_name: string;
  phone: string | null; due_date: string; days_over: number;
}

const DUE_LABEL: Record<string, string> = {
  followup_lab: "Follow up Lab Work",
  followup_consult: "Follow up Consultations",
};

/** Everyone whose Follow up Lab Work or Follow up Consultations has come up. */
function DueFromCharts() {
  const [due, setDue] = useState<DueFromChart[]>([]);

  useEffect(() => {
    let live = true;
    api.get<{ due: DueFromChart[] }>("/frontdesk/followups-due")
      .then((d) => { if (live) setDue(d.due ?? []); })
      .catch(() => { /* the queue below is the point of this screen */ });
    return () => { live = false; };
  }, []);

  if (due.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-xl border border-amber-700/40 bg-amber-500/5">
      <p className="border-b border-amber-700/30 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-amber-300">
        Due now, from their charts ({due.length})
      </p>
      <ul className="divide-y divide-amber-700/20">
        {due.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-2 text-sm">
            <Link to={`/contacts/${d.contact_id}`} className="font-medium text-zinc-100 hover:text-indigo-300">
              {d.client_name}
            </Link>
            <span className="text-zinc-400">{DUE_LABEL[d.kind] ?? "Follow-up"}</span>
            <span className="text-xs text-zinc-500">
              {d.days_over > 0
                ? `was due ${formatDate(d.due_date, { month: "short", day: "numeric" })}`
                : "due today"}
            </span>
            {d.phone && <span className="text-xs text-zinc-500">· {formatPhone(d.phone)}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FollowUpQueue() {
  const toast = useToast();
  const [view, setView] = useState<"open" | "done">("open");
  const [items, setItems] = useState<FollowUp[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<FollowUp | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get<{ follow_ups: FollowUp[] }>("/frontdesk/follow-ups", { status: view })
      .then((d) => setItems(d.follow_ups))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Couldn't load the list"))
      .finally(() => setLoading(false));
  }, [view, toast]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id: string) => {
    try {
      await api.patch(`/frontdesk/follow-ups/${id}`, { status: "done" });
      load();
    } catch (err) { toast.error(err instanceof ApiError ? err.message : "That didn't work"); }
  };
  const remove = async (id: string) => {
    try { await api.delete(`/frontdesk/follow-ups/${id}`); load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : "That didn't work"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1 rounded-lg border border-zinc-800 bg-zinc-900/50 p-0.5">
          {(["open", "done"] as const).map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={cn("rounded-md px-3 py-1 text-sm capitalize",
                view === v ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200")}>
              {v === "open" ? "Open" : "Done"}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={() => setAdding(true)}><Plus className="h-3.5 w-3.5" /> Add someone</Button>
      </div>

      <p className="rounded-lg border border-zinc-800/70 bg-zinc-900/30 px-3 py-2 text-xs text-zinc-500">
        This list is for the team only — nothing here texts or emails the client.
      </p>

      {/* The dates off the charts. Somebody whose lab work is due is, by
          definition, not booked in, so no card on the board will ever carry
          it: without this the day passes and nobody knows. */}
      <DueFromCharts />

      {loading ? <Skeleton className="h-64" />
        : items.length === 0 ? (
          <EmptyState icon={ClipboardList}
            title={view === "open" ? "Nothing to follow up" : "Nothing closed yet"}
            description={view === "open" ? "Add a client when they need a nurse review, a lab check or a call back." : ""} />
        ) : (
          <div className="overflow-hidden rounded-xl border border-zinc-800/70">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/50 text-zinc-500">
                <tr className="text-left text-[11px] uppercase tracking-wider">
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Why</th>
                  <th className="px-3 py-2 font-medium">Note</th>
                  <th className="px-3 py-2 font-medium">Added</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((f) => (
                  <tr key={f.id} className="border-t border-zinc-800/40 hover:bg-zinc-900/40">
                    <td className="px-3 py-2">
                      <Link to={`/contacts/${f.contact_id}`} className="font-medium text-zinc-100 hover:text-indigo-300">
                        {f.client_name}
                      </Link>
                      {f.phone && (
                        <p className="flex items-center gap-1 text-[11px] text-zinc-500">
                          <Phone className="h-3 w-3" />{formatPhone(f.phone)}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge color={f.urgent ? "rose" : "zinc"} size="xs">
                        {REASON_LABEL[f.reason] ?? f.reason}
                      </Badge>
                    </td>
                    <td className="max-w-md px-3 py-2 text-zinc-400">
                      {f.note || "—"}
                      {f.resolution && <p className="mt-0.5 text-[11px] text-emerald-400">Closed: {f.resolution}</p>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-[11px] text-zinc-500">
                      {formatDate(f.created_at, { month: "short", day: "numeric" })}
                      {f.created_by_name && <span className="block">by {f.created_by_name}</span>}
                      {f.resolved_by && <span className="block text-emerald-500">closed by {f.resolved_by}</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {view === "open" && (
                        <>
                          <Link to={`/schedule?contact=${f.contact_id}`}
                            className="mr-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-indigo-300 hover:bg-zinc-800">
                            <CalendarPlus className="h-3 w-3" /> Book
                          </Link>
                          <button onClick={() => setEditing(f)}
                            className="mr-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-zinc-300 hover:bg-zinc-800">
                            <Pencil className="h-3 w-3" /> Change
                          </button>
                          <button onClick={() => resolve(f.id)}
                            className="mr-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-emerald-300 hover:bg-zinc-800">
                            <Check className="h-3 w-3" /> Done
                          </button>
                        </>
                      )}
                      <button onClick={() => remove(f.id)}
                        className="inline-flex items-center rounded-md px-1.5 py-1 text-zinc-600 hover:bg-zinc-800 hover:text-rose-400">
                        <X className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {adding && <AddFollowUp onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {editing && (
        <AddFollowUp existing={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

/**
 * Adding somebody to the list, and changing one that is already on it.
 *
 *   "Also follow ups I need a way to change something if needed"
 *
 * The same dialog does both: picking the wrong reason, or wanting to add to
 * the note once somebody has looked, used to mean deleting the row and
 * starting again, which loses who raised it and when.
 */
function AddFollowUp({ existing, onClose, onSaved }: {
  existing?: FollowUp; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const editing = !!existing;
  const [q, setQ] = useState("");
  const [results, setResults] = useState<{ id: string; first_name: string | null; last_name: string | null }[]>([]);
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(
    existing ? { id: existing.contact_id, name: existing.client_name } : null);
  const [reason, setReason] = useState(existing?.reason ?? "provider_review");
  const [note, setNote] = useState(existing?.note ?? "");
  const [urgent, setUrgent] = useState(existing?.urgent ?? false);
  const [saving, setSaving] = useState(false);
  const [searched, setSearched] = useState(false);

  /**
   * "I have a patient who is in the contacts but I am unable to pull her up …
   *  it appears to be only options of certain names no matter what name I
   *  search"
   *
   * It was sending `search=`; the client list reads `q`. So the name was
   * thrown away and the same first handful of people came back whatever she
   * typed - which looks like a broken list rather than an ignored search.
   */
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setSearched(false); return; }
    const t = setTimeout(() => {
      api.get<{ contacts: typeof results }>("/contacts", { q: q.trim(), limit: 12 })
        .then((d) => { setResults(d.contacts); setSearched(true); })
        .catch(() => { setResults([]); setSearched(true); });
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const save = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      if (existing) await api.patch(`/frontdesk/follow-ups/${existing.id}`, { reason, note, urgent });
      else await api.post("/frontdesk/follow-ups", { contact_id: picked.id, reason, note, urgent });
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : editing ? "Couldn't save that" : "Couldn't add that");
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={editing ? "Change this follow-up" : "Add to the follow-up list"}>
      <div className="space-y-3">
        {picked ? (
          <div className="flex items-center justify-between rounded-lg border border-zinc-700 bg-zinc-900/60 px-3 py-2">
            <span className="text-sm text-zinc-100">{picked.name}</span>
            {/* Who it is about stays put when editing: that is a different
                follow-up, not a change to this one. */}
            {!editing && (
              <button onClick={() => setPicked(null)} className="text-xs text-zinc-500 hover:text-zinc-300">change</button>
            )}
          </div>
        ) : (
          <div>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for a client…" autoFocus />
            {results.length > 0 && (
              <ul className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-zinc-800">
                {results.map((c) => {
                  const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || "Unnamed";
                  return (
                    <li key={c.id}>
                      <button onClick={() => { setPicked({ id: c.id, name }); setQ(""); }}
                        className="w-full px-3 py-1.5 text-left text-sm text-zinc-300 hover:bg-zinc-800">
                        {name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {/* Say so, rather than showing a stale list that looks like the
                answer. */}
            {searched && results.length === 0 && q.trim().length >= 2 && (
              <p className="mt-1.5 text-xs text-zinc-500">
                Nobody matching "{q.trim()}". Check the spelling, or add them under Clients first.
              </p>
            )}
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs text-zinc-400">Why</label>
          <Select value={reason} onChange={(e) => setReason(e.target.value)}>
            {/* Only the six she asked for are offered; the older ones still
                render on rows raised before, they are just not choices. */}
            {REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-xs text-zinc-400">Note for the team</label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            placeholder="e.g. Final blood uploaded to her file — nurses to review" />
        </div>

        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)}
            className="rounded border-zinc-600 bg-zinc-800" />
          Urgent — put it at the top
        </label>
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={!picked || saving}>{editing ? "Save" : "Add to list"}</Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Paperwork: day list + the review pile ─────────────────────────────────

// Two different piles, because two different people clear them: paperwork
// is read by a provider, and a chart only reaches the medical director when
// a provider ticks the box on it.
/**
 * "The drop down list is Medical Director Review / Appointment Documents Not
 *  Completed. That's it for that for now."
 *
 * Two piles, in her words. The rest of the filters the page used to carry
 * (unsigned charts, visits with no chart, everything that day) are still real
 * questions, but she does not work from them, and seven choices made the two
 * that matter harder to find.
 */
const FILTERS: { key: string; label: string }[] = [
  { key: "charts_for_md", label: "Medical Director Review" },
  // A list of paperwork waiting on a provider lived here briefly. She asked
  // for it gone: "that added section off the front desk is great but not
  // needed because the providers will be in the patient chart to look at that
  // paperwork then they get the sign off box". The filter still exists in the
  // api for anyone who wants it; the desk does not work from it.
  { key: "forms_outstanding", label: "Appointment Documents Not Completed" },
];

function Paperwork() {
  const toast = useToast();
  const [date, setDate] = useState<string | null>(null);
  // Opens on the medical director's pile, which is the one with a deadline.
  const [filter, setFilter] = useState("charts_for_md");
  const [rows, setRows] = useState<DayRow[]>([]);
  const [sweeping, setSweeping] = useState(false);
  const [tz, setTz] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get<{ rows: DayRow[]; sweeping: boolean; date: string; timezone: string }>(
      "/frontdesk/day-list", date ? { date, filter } : { filter })
      .then((d) => { setRows(d.rows); setSweeping(d.sweeping); setTz(d.timezone); if (!date) setDate(d.date); })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Couldn't load that"))
      .finally(() => setLoading(false));
  }, [date, filter, toast]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 w-72 text-sm">
          {FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </Select>
        {!sweeping && (
          <Input type="date" value={date ?? ""} onChange={(e) => setDate(e.target.value)} className="h-8 w-40 text-sm" />
        )}
        {sweeping && (
          <span className="text-xs text-zinc-500">Showing everything outstanding, not just one day.</span>
        )}
      </div>

      {loading ? <Skeleton className="h-64" />
        : rows.length === 0 ? (
          /* An empty review pile is good news, and "Nothing matches that
             filter" read like the tab was broken. Say which pile is clear. */
          <EmptyState
            icon={FileText}
            title={filter === "charts_for_md"
              ? "Nothing waiting for the medical director"
              : "Everybody's paperwork is complete"}
            description={filter === "charts_for_md"
              ? "Every chart has been reviewed. A chart lands here the moment a provider sends one for sign-off, whether or not it came from a booking."
              : "Nobody has an appointment document still to fill in."}
          />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-zinc-800/70">
            <table className="w-full min-w-max text-sm">
              <thead className="bg-zinc-900/50 text-zinc-500">
                <tr className="text-left text-[11px] uppercase tracking-wider">
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Treatments</th>
                  <th className="px-3 py-2 font-medium">Provider</th>
                  <th className="px-3 py-2 font-medium">Paperwork</th>
                  <th className="px-3 py-2 font-medium">Chart</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-800/40 hover:bg-zinc-900/40">
                    <td className="whitespace-nowrap px-3 py-2 text-zinc-400">
                      {formatDate(r.starts_at, { month: "short", day: "numeric" })} {timeIn(r.starts_at, tz)}
                    </td>
                    <td className="px-3 py-2">
                      {/* "Medical Director review is it possible to have the
                          client names pop up so she can go directly to chart
                          and sign off in chart notes" — the name lands on the
                          notes themselves, not the overview. */}
                      {r.contact_id ? (
                        <Link
                          to={`/contacts/${r.contact_id}?tab=charts`}
                          className="font-medium text-zinc-100 hover:text-indigo-300"
                        >
                          {r.client_name}
                        </Link>
                      ) : r.client_name}
                    </td>
                    <td className="max-w-xs truncate px-3 py-2 text-zinc-400">{r.services}</td>
                    <td className="px-3 py-2 text-zinc-400">{r.providers}</td>
                    <td className="px-3 py-2">
                      {/* Paperwork she owes vs paperwork the medical director
                          still has to read — NY State wants the second one. */}
                      {r.forms_total === 0 ? <span className="text-zinc-600">—</span>
                        : r.forms_completed < r.forms_total
                          ? <Badge color="rose" size="xs">{r.forms_completed}/{r.forms_total}</Badge>
                          : r.forms_awaiting_review > 0
                            ? <Badge color="amber" size="xs">Needs review</Badge>
                            : <Badge color="emerald" size="xs">Done</Badge>}
                    </td>
                    <td className="px-3 py-2">
                      {r.charts_total === 0 ? <Badge color="zinc" size="xs">No chart</Badge>
                        : r.charts_awaiting_review > 0 ? <Badge color="amber" size="xs">Needs review</Badge>
                        : r.charts_signed >= r.charts_total ? <Badge color="emerald" size="xs">Signed</Badge>
                        : <Badge color="sky" size="xs">Draft</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

// ─── Consultations that have run out ───────────────────────────────────────
/**
 * "Except the IV has to do a renewal consult every 6 months from the last.
 * Can we automatically add that in so there is a reminder of when renewal
 * consults are needed."
 *
 * The booking page already stops a client whose consultation has lapsed.
 * This is the same rule read forwards, so the desk can ring them first.
 */
interface ConsultDueRow {
  id: string; name: string; phone: string | null; email: string | null;
  last_consult: string | null; last_treatment: string | null;
  due_at: string | null; overdue: boolean;
}

function ConsultsDue() {
  const [rows, setRows] = useState<ConsultDueRow[] | null>(null);
  const [within, setWithin] = useState(30);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRows(null);
    api.get<{ clients: ConsultDueRow[] }>("/frontdesk/consults-due", { within_days: within })
      .then((d) => setRows(d.clients))
      .catch((e) => setError(e instanceof ApiError ? e.message : "Couldn't load that"));
  }, [within]);

  if (error) return <p className="text-sm text-rose-300">{error}</p>;
  if (!rows) return <Skeleton className="h-40" />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={String(within)} onChange={(e) => setWithin(Number(e.target.value))} className="w-56">
          <option value="0">Already lapsed</option>
          <option value="30">Lapsed or due in 30 days</option>
          <option value="60">Lapsed or due in 60 days</option>
          <option value="90">Lapsed or due in 90 days</option>
        </Select>
        <p className="text-xs text-zinc-500">
          Clients who come in for a treatment that needs a consultation, and whose last one has run out.
          Anyone with a consultation already booked is left off.
        </p>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="Nobody's consultation is due" description="Everyone who comes in is inside the window." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-zinc-800/70">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2 font-medium">Client</th>
                <th className="px-3 py-2 font-medium">Last consultation</th>
                <th className="px-3 py-2 font-medium">Due</th>
                <th className="px-3 py-2 font-medium">Reach them</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {rows.map((r) => (
                <tr key={r.id} data-consult-due className="hover:bg-zinc-900/40">
                  <td className="px-3 py-2">
                    <Link to={`/contacts/${r.id}`} className="font-medium text-zinc-100 hover:text-indigo-300">{r.name}</Link>
                  </td>
                  <td className="px-3 py-2 text-zinc-400">{r.last_consult ? formatDate(r.last_consult) : "—"}</td>
                  <td className="px-3 py-2">
                    {r.due_at && (
                      <Badge color={r.overdue ? "rose" : "amber"} size="xs">
                        {r.overdue ? "Lapsed " : "Due "}{formatDate(r.due_at)}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-zinc-400">
                    {r.phone ? formatPhone(r.phone) : r.email ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/contacts/${r.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-xs font-medium text-zinc-200 hover:border-zinc-600 hover:text-white"
                    >
                      <CalendarPlus className="h-3 w-3" /> Open their file
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
